import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, MapPin, Mail, Phone, Loader2 } from "lucide-react";
import { HOME } from "@/constants/testIds";
import { OFFICES, CONTACT } from "@/lib/mockData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

const fieldCls =
  "w-full bg-white border border-brand-border rounded-lg px-4 py-3 text-brand-ink placeholder:text-brand-muted focus:outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/15 transition-colors";
const labelCls = "block text-xs font-display font-semibold uppercase tracking-wider text-brand-slate mb-2";

const Contact = () => {
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const data = Object.fromEntries(new FormData(e.currentTarget).entries());
    try {
      await axios.post(`${API}/contact-messages`, data);
      toast.success("Message sent. Expect a reply within one business day.");
      e.target.reset();
    } catch (err) {
      toast.error("Message failed to send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative py-20 md:py-28 bg-brand-surface">
      <div className="px-5 md:px-10 lg:px-16">
        <div className="flex flex-col items-center text-center mb-14 md:mb-20">
          <div className="text-xs md:text-sm font-display font-semibold tracking-[0.2em] uppercase text-brand-red mb-3">
            Get In Touch
          </div>
          <h2 className="section-heading section-heading-center font-display font-bold text-4xl md:text-5xl text-brand-ink">
            Partner with a Trusted Recruitment Agency
          </h2>
          <p className="mt-6 max-w-2xl text-brand-slate text-base md:text-lg leading-relaxed">
            Permanent recruitment, contract staffing, executive search, or RPO —
            our team delivers customized hiring solutions across the US, Europe,
            and Canada. Contact us to discuss your goals.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">
          {/* Offices column */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease: EASE }}
            className="lg:col-span-5 flex flex-col gap-4 md:gap-6"
          >
            {OFFICES.map((o) => (
              <div key={o.country} className="bg-white rounded-2xl border border-brand-border p-6 md:p-8 card-soft">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-lg bg-brand-blue-light flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-brand-blue" strokeWidth={2} />
                  </div>
                  <div>
                    <div className="text-xs font-display font-semibold uppercase tracking-wider text-brand-blue mb-2">
                      {o.country}
                    </div>
                    <div className="font-display font-bold text-brand-ink text-xl md:text-2xl leading-tight">
                      {o.address}
                    </div>
                  </div>
                </div>
              </div>
            ))}

            <div className="bg-white rounded-2xl border border-brand-border p-6 md:p-8 card-soft">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-lg bg-brand-red-light flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-brand-red" strokeWidth={2} />
                </div>
                <div className="min-w-0">
                  <div className="text-xs font-display font-semibold uppercase tracking-wider text-brand-red mb-2">
                    Email
                  </div>
                  <a
                    href={`mailto:${CONTACT.email}`}
                    className="font-display font-bold text-brand-ink text-lg md:text-xl leading-tight break-all hover:text-brand-blue transition-colors"
                  >
                    {CONTACT.email}
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-brand-border p-6 md:p-8 card-soft">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-lg bg-brand-blue-light flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-brand-blue" strokeWidth={2} />
                </div>
                <div className="min-w-0">
                  <div className="text-xs font-display font-semibold uppercase tracking-wider text-brand-blue mb-2">
                    Phone
                  </div>
                  <a
                    href={`tel:${CONTACT.phone.replace(/[^0-9+]/g, "")}`}
                    className="font-display font-bold text-brand-ink text-lg md:text-xl leading-tight hover:text-brand-red transition-colors"
                  >
                    {CONTACT.phone}
                  </a>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Contact form */}
          <motion.form
            data-testid={HOME.contactForm}
            onSubmit={submit}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.1 }}
            className="lg:col-span-7 bg-white rounded-2xl border border-brand-border p-6 md:p-10 card-soft"
          >
            <h3 className="font-display font-bold text-2xl md:text-3xl text-brand-ink mb-6">
              Send us a message
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
              <div>
                <label className={labelCls}>Your Name</label>
                <input
                  required
                  name="name"
                  placeholder="Full name"
                  className={fieldCls}
                  data-testid={HOME.contactFormName}
                />
              </div>
              <div>
                <label className={labelCls}>Your Email</label>
                <input
                  required
                  type="email"
                  name="email"
                  placeholder="you@email.com"
                  className={fieldCls}
                  data-testid={HOME.contactFormEmail}
                />
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>Message</label>
                <textarea
                  required
                  name="message"
                  rows={5}
                  placeholder="Tell us a little about what you need."
                  className={`${fieldCls} resize-none`}
                  data-testid={HOME.contactFormMessage}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid={HOME.contactFormSubmit}
              className="mt-6 md:mt-8 inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-brand-blue hover:bg-brand-blue-dark text-white font-display font-semibold rounded-md transition-colors disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {loading ? "Sending…" : "Send Message"}
              {!loading && <ArrowRight className="w-4 h-4" strokeWidth={2} />}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
